// import {Neighborhood} from ../neighborhoods/types
import { Address } from '../order/types';
/**
 * Action types
 * @LOAD_MERCHANT load merchant data to redux
 * @HANDLE_EXIST if no exist merchant
 * @LOAD_CATEGORIES load categories data to redux
 */
export enum MerchantsTypes {
  LOAD_MERCHANT = '@merchants/LOAD_MERCHANT',
  HANDLE_EXIST = '@merchants/HANDLE_EXIST',
  LOAD_CATEGORIES = '@merchants/LOAD_CATEGORIES',
  CLEAR_MERCHANT = '@merchants/CLEAR_MERCHANT',
  UPDATE_MERCHANT_AVAILABILITY = '@merchants/UPDATE_MERCHANT_AVAILABILITY',
  UPDATE_MERCHANT_CATEGORY_AVAILABILITY = '@merchants/UPDATE_MERCHANT_CATEGORY_AVAILABILITY',
  UPDATE_MERCHANT_PRODUCT_AVAILABILITY = '@merchants/UPDATE_MERCHANT_PRODUCT_AVAILABILITY',
  UPDATE_MERCHANT_PRODUCT = '@merchants/UPDATE_MERCHANT_PRODUCT',
  UPDATE_MERCHANT_PRODUCT_CURRENT_STOCK = '@merchants/UPDATE_MERCHANT_PRODUCT_CURRENT_STOCK',
}

/**
 * Data types
 * @id : id of merchant
 * @urlCode : url of merchant
 * @status : status of avaiability
 * @name : name of merchant
 * @cnpj : cnpj of merchant
 * @phoneDdd : ddd of phone
 * @phoneNumber : number of phone
 * @email : email of merchant
 * @logoPath : path of logo
 * @bannerPath : path of banner
 * @about : about merchant
 * @whatsApp : whatsapp number
 * @instagram : instagram user
 * @facebook : facebook user
 * @active : status of operation
 * @deliveryTimeMin : min time of delivery
 * @deliveryTimeMax : max time of delivery
 * @deliveryFeeMin : min value of fee
 */

export interface Merchant {
  id: string;
  urlCode: string;
  status: string;
  name: string;
  cnpj: string;
  phoneDdd: string;
  phoneNumber: string;
  email: string;
  logoPath: string;
  bannerPath: string;
  about: string;
  whatsappDdd: string;
  whatsappNumber: string;
  instagram: string;
  facebook: string;
  active: boolean;
  deliveryTimeMin: number;
  deliveryTimeMax: number;
  deliveryFeeMin: number;
  orderCpfIsRequired: boolean;
  ratings: {
    average: number,
    number: number,
    type: string
  };
  settings: {
    deliveryTimeMin: number;
    deliveryTimeMax: number;
    deliveryFeeMin: number;
    softcomShip: {
      active: boolean;
      clientId: string;
      clientSecret: string;
    };
  };
}

/**
 * Data types
 * @code : code of segment
 * @name : name of market segment
 * */

export interface MarketSegment {
  code: string;
  name: string;
}

/**
 * Data types
 * @id : id of availability
 * @weekday : day of week
 * @hourStart : initial hour
 * @hourEnd : end hour
 * */

export interface Availability {
  id: string;
  weekday: string;
  hourStart: string;
  hourEnd: string;
}

export interface Availabilities {
  avaiability: Availability[];
}

/**
 * Data types
 * @id : id of delivery method
 * @method : method of delivery
 * @description : description of delivery method
 * @instructions : instructions of delivery method
 * @orderMinimumValue : minimum value of order
 * */

export interface DeliveryMethod {
  id: string;
  method: string;
  description: string;
  instructions?: string;
  orderMinimumValue: number;
}

export interface DeliveryMethods {
  deliveryMethods: DeliveryMethod[];
}

/**
 * Data types
 * @id : id of delivery area
 * @neighborhood : name of neighborhood
 * @deliveryFee : cost of delivery for this neighborhood
 * @deliveryTime : aproach time in minutes to delivery
 * */

export interface DeliveryArea {
  id: string;
  neighborhood: string;
  deliveryFee: number;
  deliveryTime: number;
  city: string;
  state: string;
}

export interface DeliveryAreas {
  deliveryAreas: DeliveryArea[];
}

/**
 * Data types
 * @id : id of payment type
 * @code : code of payment type
 * @description : description of payment card
 * @prepaid : online payment or not
 * @iconPath : url of icon payment
 * @active : state of payment type
 * */

export interface Payment {
  id: string;
  code: string;
  description: string;
  prepaid: boolean;
  iconPath: null;
  active: boolean;
}

export interface Payments {
  payments: Payment[];
}

export interface Image {
  description: null;
  path: string;
  default?: boolean;
}

export interface Promotion {
  value: number;
  validity: string;
  originalValue: number;
  percent: number;
}

export interface VariationItem {
  variationId: string;
  id: string;
  code: string;
  name: string;
  description: string;
  value: number;
  colorHex: string;
  availability: string;
  priority: number;
  stock: {
    active: boolean;
    current: number;
    min: number;
  };
}

export interface Variation {
  productId: string;
  id: string;
  name: string;
  required: boolean;
  itemsMin: number;
  itemsMax: number;
  colorHex: string;
  priority: number;
  items: VariationItem[];
  availability: string;
}

export interface Product {
  merchantId: string;
  categoryId: string;
  id: string;
  code: string;
  ean: null;
  priority: number;
  name: string;
  description: string;
  price: number;
  availability: string;
  measure: string;
  merchantSuggestion: boolean;
  isPromotion: boolean;
  promotion: Promotion;
  stock: Stock;
  images: Image[];
  variationsGrid: boolean;
  variations: Variation[];
  imagePath?: string;
}

export interface Stock {
  active: boolean;
  current: number;
  min: number;
  orderMax: number;
}

export interface Category {
  merchantId: string;
  id: string;
  code: null;
  name: string;
  priority: 0;
  availability: string;
  products: Product[];
}

/**
 * State type
 * @data : the Address
 */
export interface MerchantsState {
  id: string;
  urlCode: string;
  status: string;
  name: string;
  cnpj: string;
  phoneDdd: string;
  phoneNumber: string;
  email: string;
  logoPath: string;
  bannerPath: string;
  about: string;
  whatsappDdd: string;
  whatsappNumber: string;
  instagram: string;
  facebook: string;
  active: boolean;
  deliveryTimeMin: number;
  deliveryTimeMax: number;
  deliveryFeeMin: number;
  address: Address;
  marketSegment?: MarketSegment;
  marketSegmentName?: MarketSegment;
  marketplace: boolean;
  ratings: {
    average: number,
    number: number,
    type: string
  };
  settings: {
    deliveryTimeMin: number;
    deliveryTimeMax: number;
    deliveryFeeMin: number;
    softcomShip: {
      active: boolean;
      clientId: string;
      clientSecret: string;
    };
  };
  showProductUnavailableInApp: boolean;
  availability: Availability[];
  deliveryMethods: DeliveryMethod[];
  deliveryAreas: DeliveryArea[];
  payments: Payment[];
  loaded: boolean;
  exist: boolean;
  categories: Category[];
  paymentInstructions: string;
  orderCpfIsRequired: boolean;
  cardInstallmentsMax: number;
  cardInstallmentsMinValue: number;
  sitefMerchantId: null;
  sitefMerchantKey: null;
  softcomCode: string;
  integrationApp: boolean;
}
